import { Star, Quote } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useReviews } from "@/lib/useReviews";

const Stars = ({ n, className = "w-3.5 h-3.5" }) => (
  <span className="inline-flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`${className} ${i <= Math.round(n) ? "fill-[#FFB800] text-[#FFB800]" : "text-zinc-600"}`} />
    ))}
  </span>
);

const GoogleG = ({ className = "w-3.5 h-3.5" }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden>
    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7A21.99 21.99 0 0024 46z"/>
    <path fill="#FBBC05" d="M11.69 28.18A13.2 13.2 0 0111 24c0-1.45.25-2.86.69-4.18v-5.7H4.34A21.99 21.99 0 002 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"/>
    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.94 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/>
  </svg>
);

export function ReviewsSection() {
  const { t } = useLang();
  const { reviews, avg, total, googleAvailable, hasAny, writeReviewUrl } = useReviews();
  if (!hasAny) return null;

  return (
    <section id="avis" className="relative py-16 border-t border-white/10" data-testid="reviews-section">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <span className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-[#00E5FF]">{t("reviews.overline")}</span>
            <h2 className="font-display font-900 text-white text-2xl sm:text-3xl tracking-tight uppercase mt-2 leading-none">{t("reviews.title")}</h2>
          </div>
          <div className="flex items-center gap-3 border border-white/10 bg-[#0C0C0E] px-4 py-2.5" data-testid="reviews-aggregate">
            {googleAvailable && <GoogleG className="w-5 h-5" />}
            <span className="font-display font-900 text-white text-2xl leading-none">{avg.toFixed(1)}</span>
            <div className="flex flex-col gap-0.5">
              <Stars n={avg} className="w-3 h-3" />
              <span className="font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500">{total} {t("reviews.count_label")}</span>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {reviews.slice(0, 9).map((r) => (
            <div key={r.id} className="bg-[#0C0C0E] border border-white/10 p-4 flex flex-col gap-2.5" data-testid={`review-card-${r.id}`}>
              <div className="flex items-center justify-between">
                {r.source === "google" ? <GoogleG className="w-4 h-4" /> : <Quote className="w-5 h-5 text-[#E60000]/60" />}
                <Stars n={r.rating} className="w-3 h-3" />
              </div>
              <p className="text-zinc-300 text-[13px] leading-relaxed flex-1 line-clamp-5">“{r.text}”</p>
              <div className="pt-2.5 border-t border-white/5 flex items-center gap-2.5">
                {r.profile_photo ? (
                  <img src={r.profile_photo} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" referrerPolicy="no-referrer" />
                ) : null}
                <div className="min-w-0">
                  <p className="font-display font-700 text-white text-sm truncate">{r.name}</p>
                  {r.source === "google" ? (
                    <p className="font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5 flex items-center gap-1"><GoogleG className="w-3 h-3" /> {t("reviews.via_google")}{r.relative_time ? ` · ${r.relative_time}` : ""}</p>
                  ) : r.vehicle ? (
                    <p className="font-mono-tech text-[10px] uppercase tracking-wider text-[#00E5FF] mt-0.5 truncate">{r.vehicle}</p>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex justify-center">
          <a href={writeReviewUrl} target="_blank" rel="noopener noreferrer" data-testid="leave-google-review"
            className="group inline-flex items-center gap-2.5 border border-white/15 bg-[#0C0C0E] hover:border-[#00E5FF]/60 hover:bg-[#00E5FF]/5 transition-colors px-5 py-3">
            <GoogleG className="w-4 h-4" />
            <span className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-white group-hover:text-[#00E5FF] transition-colors">{t("reviews.leave_google")}</span>
          </a>
        </div>
      </div>
    </section>
  );
}
